import type { HealthSample } from '@occ/contracts';
import { useId } from 'react';
import { percent } from '../lib/format';

/**
 * Gráfica de la serie de salud (Módulo 7).
 *
 * SVG escrito a mano y **no Recharts**, que es lo que sugiere el PRD. La razón no
 * es técnica sino de coordinación: añadir una librería de gráficas toca el
 * lockfile de la raíz, que ahora mismo comparten cuatro sesiones trabajando en
 * paralelo, y un conflicto ahí bloquea a todas. Lo que hace falta aquí —tres
 * series de 0 a 100 sobre el tiempo, con rejilla y último valor— cabe en cien
 * líneas; cuando Recharts entre, este componente se sustituye entero y nadie más
 * se enterará porque la interfaz es solo `samples`.
 *
 * El eje vertical está fijo en 0–100 a propósito: son porcentajes, y
 * autoescalarlo haría que un 3 % de CPU llenara la caja y pareciera saturación.
 */

const WIDTH = 640;
const HEIGHT = 180;
const PADDING = { top: 12, right: 8, bottom: 20, left: 30 };

const PLOT_WIDTH = WIDTH - PADDING.left - PADDING.right;
const PLOT_HEIGHT = HEIGHT - PADDING.top - PADDING.bottom;

type SeriesKey = 'cpuPercent' | 'memoryUsedPercent' | 'diskUsedPercent';

const SERIES: Array<{ key: SeriesKey; label: string; color: string; filled: boolean }> = [
  // La CPU va en el color de marca porque es la que se mira primero; las otras
  // dos en tonos que se distinguen tanto en claro como en oscuro sin depender del
  // tema, que es justo lo que el branding del tenant puede cambiar bajo los pies.
  { key: 'cpuPercent', label: 'CPU', color: 'var(--occ-brand)', filled: true },
  { key: 'memoryUsedPercent', label: 'Memoria', color: '#f59e0b', filled: false },
  { key: 'diskUsedPercent', label: 'Disco', color: '#14b8a6', filled: false },
];

const xOf = (index: number, count: number): number =>
  // Con una sola muestra no hay recta que dibujar: se centra el punto en vez de
  // dividir por cero.
  count <= 1 ? PADDING.left + PLOT_WIDTH / 2 : PADDING.left + (index / (count - 1)) * PLOT_WIDTH;

const yOf = (value: number): number =>
  PADDING.top + PLOT_HEIGHT - (Math.min(100, Math.max(0, value)) / 100) * PLOT_HEIGHT;

const hourMinute = (date: Date): string =>
  date.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });

export function HealthChart({ samples }: { samples: HealthSample[] }) {
  const gradientId = useId();

  if (samples.length === 0) {
    return <p className="text-sm opacity-60">Sin muestras que dibujar todavía.</p>;
  }

  const first = samples[0];
  const last = samples.at(-1);

  return (
    <div className="grid gap-2">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="h-auto w-full"
        role="img"
        aria-label="Serie de CPU, memoria y disco del nodo"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--occ-brand)" stopOpacity="0.28" />
            <stop offset="100%" stopColor="var(--occ-brand)" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Rejilla cada 25 %. `currentColor` con opacidad se adapta al tema sin
            duplicar el color en claro y en oscuro. */}
        {[0, 25, 50, 75, 100].map((tick) => (
          <g key={tick}>
            <line
              x1={PADDING.left}
              x2={WIDTH - PADDING.right}
              y1={yOf(tick)}
              y2={yOf(tick)}
              stroke="currentColor"
              strokeOpacity={tick === 0 ? 0.25 : 0.1}
              strokeWidth={1}
            />
            <text
              x={PADDING.left - 6}
              y={yOf(tick) + 3}
              textAnchor="end"
              fontSize={9}
              fill="currentColor"
              fillOpacity={0.5}
            >
              {tick}
            </text>
          </g>
        ))}

        {SERIES.map((series) => {
          // Las muestras pueden traer huecos: `cpuPercent` es opcional en el
          // contrato. Se salta el punto en vez de tratarlo como cero, que
          // dibujaría una caída al suelo que no ocurrió.
          const points = samples
            .map((sample, index) => ({ value: sample[series.key], index }))
            .filter((point): point is { value: number; index: number } => point.value !== undefined)
            .map((point) => `${xOf(point.index, samples.length)},${yOf(point.value)}`);

          if (points.length === 0) return null;
          const path = points.join(' ');

          return (
            <g key={series.key}>
              {series.filled && (
                <polygon
                  points={`${PADDING.left},${yOf(0)} ${path} ${WIDTH - PADDING.right},${yOf(0)}`}
                  fill={`url(#${gradientId})`}
                />
              )}
              <polyline
                points={path}
                fill="none"
                stroke={series.color}
                strokeWidth={1.75}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </g>
          );
        })}

        {first && (
          <text x={PADDING.left} y={HEIGHT - 6} fontSize={9} fill="currentColor" fillOpacity={0.5}>
            {hourMinute(first.sampledAt)}
          </text>
        )}
        {last && samples.length > 1 && (
          <text
            x={WIDTH - PADDING.right}
            y={HEIGHT - 6}
            textAnchor="end"
            fontSize={9}
            fill="currentColor"
            fillOpacity={0.5}
          >
            {hourMinute(last.sampledAt)}
          </text>
        )}
      </svg>

      <div className="flex flex-wrap gap-4 text-xs">
        {SERIES.map((series) => (
          <span key={series.key} className="inline-flex items-center gap-1.5">
            <span
              aria-hidden
              className="inline-block h-0.5 w-4 rounded"
              style={{ background: series.color }}
            />
            <span className="opacity-70">{series.label}</span>
            <span className="font-medium">{percent(last?.[series.key])}</span>
          </span>
        ))}
        <span className="opacity-50">
          {samples.length} {samples.length === 1 ? 'muestra' : 'muestras'}
        </span>
      </div>
    </div>
  );
}
